import React, { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import './productItem.css';

const fallbackProductImage =
  'https://encrypted-tbn2.gstatic.com/shopping?q=tbn:ANd9GcQ9_LVcKEev1SQM_WkDTDqsr6Qz0JfvJhgqianTiu6gibzSe_Ccc9NrkaGMV1MSl6Mp1ex-rk-tn0pI5_--apXnvwt4TDrnfcOf297Bwd7DNY9i5qFwxJPd2A';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const API_ORIGIN = (() => {
  try {
    return new URL(API_BASE_URL).origin;
  } catch {
    return '';
  }
})();

const resolveProductImage = (image) => {
  if (!image) {
    return fallbackProductImage;
  }

  const normalizedImage = `${image}`.replace(/\\/g, '/').trim();
  if (!normalizedImage) {
    return fallbackProductImage;
  }

  if (/^(https?:)?\/\//i.test(normalizedImage) || normalizedImage.startsWith('data:')) {
    return normalizedImage;
  }

  if (!API_ORIGIN) {
    return normalizedImage.startsWith('/') ? normalizedImage : `/${normalizedImage}`;
  }

  return normalizedImage.startsWith('/')
    ? `${API_ORIGIN}${normalizedImage}`
    : `${API_ORIGIN}/${normalizedImage}`;
};

function ProductItem({
  image,
  name,
  price,
  extraAttributes,
  onClick,
  onAddToCart,
  isOnSale,
  originalPrice,
  condition,
  cardVariantClass,
  badgeLabel,
  productPath,
}) {
  const productCardRef = useRef(null);
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('card-animate');
        }
      },
      { threshold: 0.1 }
    );

    const node = productCardRef.current;
    if (node) {
      observer.observe(node);
    }

    return () => {
      if (node) {
        observer.unobserve(node);
      }
    };
  }, []);

  const imageUrl = resolveProductImage(image);

  const specsText = useMemo(() => {
    const values = extraAttributes
      ? Object.values(extraAttributes)
          .filter((attr) => attr?.is_displayable && attr?.value)
          .map((attr) => attr.value)
      : [];

    const parts = [];
    if (condition) {
      parts.push(condition);
    }
    parts.push(...values.slice(0, 1));

    return parts.join(' - ');
  }, [condition, extraAttributes]);

  const tag = badgeLabel || (isOnSale && originalPrice ? 'Promo' : '');
  const displayTag = tag === 'Promo' ? t('home.sale') : tag === 'Top vente' ? t('home.topSale') : tag;

  return (
    <article
      ref={productCardRef}
      className={`card${tag ? ' has-badge' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      }}
    >
      {tag && (
        <div className={`card-sale-badge ${tag === 'Promo' ? 'card-sale-badge-ribbon' : ''}`}>
          {displayTag}
        </div>
      )}

      <div className={`card-upper ${cardVariantClass || ''}`}>
        <div className="card-image-frame">
          <img src={imageUrl} alt={name} className="card-image" loading="lazy" />
        </div>
      </div>

      <div className="card-lower">
        <h4 className="card-title">
          {productPath ? (
            <Link to={productPath} onClick={(event) => event.stopPropagation()}>{name}</Link>
          ) : name}
        </h4>
        <p className="product-attributes-item">{specsText || t('common.product')}</p>

        <div className="card-price-container">
          <p className="card-price">{price} DH</p>
          {isOnSale && originalPrice && (
            <p className="card-original-price">
              <del>{originalPrice} DH</del>
            </p>
          )}
        </div>

        <div className="card-actions-row">
          {productPath ? (
            <Link
              className="card-action"
              to={productPath}
              onClick={(event) => event.stopPropagation()}
            >
              {t('common.viewProduct')}
            </Link>
          ) : (
            <button
              type="button"
              className="card-action"
              onClick={(event) => {
                event.stopPropagation();
                onClick();
              }}
            >
              {t('common.viewProduct')}
            </button>
          )}
          <button
            type="button"
            className="card-action card-action-secondary"
            onClick={(event) => {
              event.stopPropagation();
              if (onAddToCart) onAddToCart();
            }}
          >
            <i className="fas fa-shopping-cart" aria-hidden="true" />
            {t('product.addToCart')}
          </button>
        </div>
      </div>
    </article>
  );
}

export default ProductItem;
